import { Injectable } from '@angular/core';
import { DateFormatService } from '@app/core/date/date-format.service';
import { IProductAllocation } from '@app/core/product/models/product-allocation.model';
import { IProductInfo } from '@app/core/product/models/product-info.model';
import { IProductPackageItem } from '@app/core/product/models/product-package-item.model';
import { IProductSession, TWhenAttendingProductSession } from '@app/core/product/models/product-session.model';
import { ISessionDiscount } from '@app/core/product/models/product-session-discount.model';

@Injectable({
  providedIn: 'root'
})
export class WhenAttendingSessionService {
  constructor(private dateFormatService: DateFormatService) {}

  /**
   * Compares two session groups to determine if they are the same.
   * @param session first group to compare
   * @param productSession second group to compare
   * @returns true if both sessions pass all compare conditions
   */
  isSameSessionGroup(
    session: TWhenAttendingProductSession | undefined,
    productSession: TWhenAttendingProductSession | undefined
  ): boolean {
    if (!session || !productSession) {
      return false;
    }

    if (!this.dateFormatService.isSameDay(session.date, productSession.date)) {
      return false;
    }

    return session.startTime === productSession.startTime && session.name === productSession.name;
  }

  /**
   * From a user-selected session time/name, get the individual IProductSession that applied to a given ticket
   * For packages, this will be the first package session that has an allocation that matches the package item.
   * For everything else, this will only ever be a single selected session.
   */
  getSelectedSession(
    session: TWhenAttendingProductSession | undefined,
    ticket: IProductInfo | undefined
  ): IProductSession | undefined {
    if (!session) {
      return;
    } else if (this.isProductSession(session)) {
      return session;
    }

    const packageItemProductIds = ticket?.packageItems?.map((item: IProductPackageItem) => item.productId) ?? [];

    const packageSessions = session.packageSessions.filter((session: IProductSession) =>
      session.allocations.some((allocation: IProductAllocation) => packageItemProductIds.includes(allocation.productId))
    );

    return packageSessions.at(0);
  }

  /**
   * From a user-selected session time/name, get the individual IProductSessions
   * For packages, this will be the all individual sessions for that time/name.
   * For everything else, this will only ever be a single selected session.
   */
  getSelectedSessions(session: TWhenAttendingProductSession | undefined): IProductSession[] | undefined {
    if (!session) {
      return;
    } else if (this.isProductSession(session)) {
      return [session as IProductSession];
    }

    return session.packageSessions;
  }

  getProductSessionEndTimeDateWithBuffer(
    session: TWhenAttendingProductSession,
    sessionTimeBuffer = 0,
    appliedSessionDiscount?: ISessionDiscount
  ) {
    const startTimeNumber = this.dateFormatService.hourStringToNumber(session.startTime);
    let endTimeString = this.getProductSessionEndTime(session, appliedSessionDiscount);
    if (sessionTimeBuffer) {
      endTimeString = this.dateFormatService.addMinutes(endTimeString, sessionTimeBuffer);
    }

    const endTimeNumber = this.dateFormatService.hourStringToNumber(endTimeString);
    const endTimeDate = this.dateFormatService.sessionDateToDate(session.date, endTimeString);
    // end time next day
    if (endTimeNumber < startTimeNumber) {
      endTimeDate.setDate(endTimeDate.getDate() + 1);
    }

    return endTimeDate;
  }

  getProductSessionEndTime(session: TWhenAttendingProductSession, appliedSessionDiscount?: ISessionDiscount) {
    if (this.isProductSession(session)) {
      // applied session discounts only for session passes
      return appliedSessionDiscount?.endTime || session.endTime;
    }

    const endOfTheDayNumber = 2400;
    let maxPackageSessionEndTime = session.packageSessions.reduce((max: number, current: IProductSession) => {
      const startTime = this.dateFormatService.hourStringToNumber(current.startTime);
      const endTime = this.dateFormatService.hourStringToNumber(current.endTime);
      const normalizedEndTime = endTime <= startTime ? endTime + endOfTheDayNumber : endTime;
      return Math.max(max, normalizedEndTime);
    }, 0);

    if (maxPackageSessionEndTime >= endOfTheDayNumber) {
      maxPackageSessionEndTime -= endOfTheDayNumber;
    }

    return this.dateFormatService.numberToHourString(maxPackageSessionEndTime);
  }

  private isProductSession(session: TWhenAttendingProductSession): session is IProductSession {
    return !!(session as IProductSession).endTime;
  }
}
